import { useEffect, useState, useRef } from "react";
import { Upload, Download, Trash2, FileText, CheckCircle2, XCircle, Scale, FileSpreadsheet, Image as ImgIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ACCEPTED_EXTS, formatDate, formatSize, SUB_ACCOUNTS } from "@/lib/constants";
import { uploadDocument, listDocuments, deleteDocument, downloadDocumentUrl, listPayments } from "@/lib/api";
import FinancialPanel from "@/components/FinancialPanel";
import ReportingPanel from "@/components/ReportingPanel";
import DosyaBilgileriForm from "@/components/DosyaBilgileriForm";

function FileIcon({ name }) {
  const ext = (name || "").split(".").pop()?.toLowerCase();
  if (["pdf"].includes(ext)) return <FileText className="w-4 h-4 text-red-400" />;
  if (["doc", "docx", "udf", "txt"].includes(ext)) return <FileText className="w-4 h-4 text-blue-400" />;
  if (["xls", "xlsx"].includes(ext)) return <FileSpreadsheet className="w-4 h-4 text-emerald-400" />;
  if (["jpg", "jpeg", "png", "gif"].includes(ext)) return <ImgIcon className="w-4 h-4 text-amber-400" />;
  return <FileText className="w-4 h-4 text-slate-400" />;
}

function Dropzone({ caseId, subAccount, label, onUploaded, testid }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    try {
      await uploadDocument(caseId, subAccount, file);
      toast.success(`${file.name} yüklendi`);
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  return (
    <div
      className={`dropzone ${drag ? "drag" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      data-testid={testid}
    >
      <Upload className="w-8 h-8 text-amber-500 mx-auto mb-2" />
      <div className="text-sm text-slate-200 font-medium">{label}</div>
      <div className="text-xs text-slate-500 mt-1">{ACCEPTED_EXTS.join(", ")}</div>
      <input
        ref={inputRef} type="file" className="hidden"
        accept={ACCEPTED_EXTS.join(",")}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
      />
      <Button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="bg-amber-600 hover:bg-amber-700 text-slate-950 mt-3"
        size="sm"
      >
        {uploading ? "Yükleniyor..." : "Dosya Seç"}
      </Button>
    </div>
  );
}

function DocList({ docs, onDelete }) {
  if (!docs.length)
    return <div className="text-xs text-slate-500 italic px-3 py-6 text-center border border-dashed border-slate-800 rounded">Henüz belge yok</div>;
  return (
    <div className="space-y-2" data-testid="document-list">
      {docs.map((d) => (
        <div key={d.id} className="flex items-center gap-3 px-3 py-2 bg-slate-900/60 border border-slate-800 rounded hover:border-amber-700/60 transition">
          <FileIcon name={d.original_filename} />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-200 truncate">{d.original_filename}</div>
            <div className="text-[11px] text-slate-500 font-mono">{formatSize(d.size)} • {formatDate(d.created_at)}</div>
          </div>
          <a href={downloadDocumentUrl(d.id)} target="_blank" rel="noreferrer">
            <Button size="sm" variant="ghost" className="text-slate-300 hover:text-amber-500" data-testid={`doc-download-${d.id}`}>
              <Download className="w-4 h-4" />
            </Button>
          </a>
          <Button size="sm" variant="ghost" onClick={() => onDelete(d.id)}
            className="text-slate-400 hover:text-red-400" data-testid={`doc-delete-${d.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}

export default function SubAccountPanel({ caseData, subKey, onRefresh }) {
  const [docs, setDocs] = useState([]);
  const [payments, setPayments] = useState([]);

  const sa = SUB_ACCOUNTS.find((s) => s.key === subKey);

  const load = async () => {
    if (subKey === "raporlama") {
      const [all, pays] = await Promise.all([listDocuments(caseData.id), listPayments(caseData.id)]);
      setDocs(all); setPayments(pays);
    } else if (subKey === "vekalet_ucreti") {
      const pays = await listPayments(caseData.id);
      setPayments(pays);
    } else if (subKey === "sonuc") {
      const [lehe, aleyhe] = await Promise.all([
        listDocuments(caseData.id, "sonuc_lehe"),
        listDocuments(caseData.id, "sonuc_aleyhe"),
      ]);
      setDocs([...lehe.map((d) => ({ ...d, _side: "lehe" })), ...aleyhe.map((d) => ({ ...d, _side: "aleyhe" }))]);
    } else {
      const list = await listDocuments(caseData.id, subKey);
      setDocs(list);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [subKey, caseData.id]);

  const handleDelete = async (id) => {
    try {
      await deleteDocument(id);
      toast.success("Belge silindi");
      load(); onRefresh?.();
    } catch { toast.error("Silinemedi"); }
  };

  const onUploaded = () => { load(); onRefresh?.(); };

  // Special panels
  if (subKey === "vekalet_ucreti") {
    return <FinancialPanel caseData={caseData} payments={payments} onRefresh={() => { load(); onRefresh?.(); }} />;
  }
  if (subKey === "raporlama") {
    return <ReportingPanel caseData={caseData} docs={docs} payments={payments} />;
  }
  if (subKey === "sonuc") {
    const lehe = docs.filter((d) => d._side === "lehe");
    const aleyhe = docs.filter((d) => d._side === "aleyhe");
    return (
      <div className="space-y-6" data-testid="sonuc-panel">
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-emerald-950/30 border-emerald-800/50">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <div>
                  <div className="font-serif text-lg text-emerald-400">Nihai Lehe Karar</div>
                  <div className="text-[11px] font-mono text-emerald-700 uppercase tracking-widest">Başarılı (+) olarak kodlanacak</div>
                </div>
              </div>
              <Dropzone caseId={caseData.id} subAccount="sonuc_lehe" label="Lehe karar belgesini bu alana sürükleyin"
                onUploaded={onUploaded} testid="sonuc-lehe-dropzone" />
              <DocList docs={lehe} onDelete={handleDelete} />
            </CardContent>
          </Card>
          <Card className="bg-red-950/30 border-red-900/50">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                <div>
                  <div className="font-serif text-lg text-red-400">Nihai Aleyhe Karar</div>
                  <div className="text-[11px] font-mono text-red-800 uppercase tracking-widest">Başarısız (-) olarak kodlanacak</div>
                </div>
              </div>
              <Dropzone caseId={caseData.id} subAccount="sonuc_aleyhe" label="Aleyhe karar belgesini bu alana sürükleyin"
                onUploaded={onUploaded} testid="sonuc-aleyhe-dropzone" />
              <DocList docs={aleyhe} onDelete={handleDelete} />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Generic doc panel
  return (
    <div className="space-y-5" data-testid={`panel-${subKey}`}>
      <div className="flex items-center gap-2">
        <Scale className="w-5 h-5 text-amber-500" />
        <div className="font-serif text-xl text-slate-100">{sa?.label}</div>
        <Badge variant="outline" className="border-slate-700 text-slate-400 ml-2">{docs.length} belge</Badge>
      </div>
      {subKey === "dava_acilis" && (
        <DosyaBilgileriForm caseData={caseData} onSaved={onRefresh} />
      )}
      <Dropzone caseId={caseData.id} subAccount={subKey}
        label={`${sa?.label} belgelerini bu alana sürükleyin`} onUploaded={onUploaded}
        testid={`${subKey}-dropzone`} />
      <DocList docs={docs} onDelete={handleDelete} />
    </div>
  );
}
